import type { SortingState, Updater } from "@tanstack/react-table"
import { useCallback } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"
import { useShallow } from "zustand/react/shallow"

import type { ProductInventoryTransactionsSearchParams } from "../schemas"
import { useProductInventoryTransactionsSearchStore } from "../stores/useProductInventoryTransactionsSearchStore"

import type { InventoryByTransactionItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

const ROUTE = "/app/inventory/products/$productId/_layout/transactions"

interface UseProductInventoryTransactionListParams {
  productId: number
}

interface UseProductInventoryTransactionListReturn {
  transactions: InventoryByTransactionItem[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: ProductInventoryTransactionsSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (value: string) => void
  handleScroll: () => void
}

export const useProductInventoryTransactionList = ({
  productId,
}: UseProductInventoryTransactionListParams): UseProductInventoryTransactionListReturn => {
  const store = useProductInventoryTransactionsSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const executeSearch = useCallback(
    (client: Client, params: ProductInventoryTransactionsSearchParams) => {
      store.executeSearch(client, { ...params, productId })
    },
    [store, productId]
  )

  const loadMore = useCallback(
    (client: Client, params: ProductInventoryTransactionsSearchParams) => {
      store.loadMore(client, { ...params, productId })
    },
    [store, productId]
  )

  const {
    data: transactions,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
  } = useListPageBase<InventoryByTransactionItem, ProductInventoryTransactionsSearchParams>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
  })

  return {
    transactions,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
  }
}
